// Helpers de escrita transacional (Etapa 5 — offline-first).
//
// Toda mutação de negócio deve passar por aqui: grava a linha na tabela E
// enfileira na outbox NA MESMA TRANSAÇÃO SQLite (via `db.transaction()`).
// Se qualquer passo falhar (inclusive o enqueue), a transação inteira faz
// rollback — nunca sobra uma linha de negócio "órfã" sem outbox
// correspondente, nem uma entrada de outbox para uma linha que não existe.
//
// Importante sobre `db.transaction()` com callback assíncrono: só funciona
// de forma correta com o driver ASSÍNCRONO deste projeto
// (`drizzle-orm/sqlite-proxy`, o mesmo usado por src/db/client.ts sobre
// expo-sqlite — ver `AppDatabase` em ./outbox). Comprovado empiricamente
// antes de escrever este módulo: o driver síncrono `drizzle-orm/better-sqlite3`
// (usado nos outros testes deste projeto, ex. src/sync/outbox.test.ts)
// rejeita callback assíncrono com "Transaction function cannot return a
// promise" — por isso os testes de writes.test.ts montam o banco de teste
// com `drizzle-orm/sqlite-proxy` + a mesma ponte WebSQL fake de
// src/db/client.test.ts, exercitando o MESMO caminho de código do app real
// (não apenas um substituto síncrono).

import { and, eq, isNull } from 'drizzle-orm';
import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { getTableName } from 'drizzle-orm';
import type { SQLiteColumn, SQLiteTable } from 'drizzle-orm/sqlite-core';
import { v7 as uuidv7 } from 'uuid';
import { outbox, profiles, projects, supplies, projectSupplies, timeEntries, counters, counterEvents, projectPhotos } from './schema';
import { enqueue, type AppDatabase } from './outbox';
import { mapLocalRecordToWire } from '../sync';

// Drizzle infere o shape de insert/update por tabela a partir do tipo
// concreto e único da tabela (`TTable["_"]["columns"]`), não distribui bem
// sobre um generic com constraint de UNIÃO de várias tabelas heterogêneas
// (WritableTable/SoftDeletableTable, acima) — tentar manter esse tipo
// exato até dentro das chamadas de `.insert()/.update()/.where()` produz
// erros de overload que não refletem um bug real (comprovado ao tentar).
// Por isso as funções públicas abaixo mantêm os parâmetros/retorno
// totalmente tipados para quem chama (data/patch/id/retorno), mas usam
// este cast só para os pontos internos de chamada ao query builder do
// Drizzle, onde a tabela já foi validada pelo generic da função pública.
function asTable(table: SQLiteTable): SQLiteTable {
  return table;
}

function idColumnOf(table: SQLiteTable): SQLiteColumn {
  return (table as unknown as { id: SQLiteColumn }).id;
}

/**
 * Tabelas de negócio com `updated_at` (todas exceto `counter_events`, que é
 * append-only — ver insertCounterEvent). `insertRecord`/`updateRecord`
 * aceitam qualquer uma destas.
 */
export type WritableTable =
  | typeof profiles
  | typeof projects
  | typeof supplies
  | typeof projectSupplies
  | typeof timeEntries
  | typeof counters
  | typeof projectPhotos;

/**
 * Subconjunto de WritableTable que tem `deleted_at` (todas exceto
 * `profiles`). `softDeleteRecord` só aceita estas — bloqueado em tempo de
 * compilação, não em runtime.
 */
export type SoftDeletableTable = Exclude<WritableTable, typeof profiles>;

type OutboxOperation = 'INSERT' | 'UPDATE' | 'DELETE';

/**
 * Enfileira na outbox coalescendo com uma entrada pendente já existente
 * para a mesma (tabela, registro, operação) em vez de empilhar uma linha
 * nova a cada chamada — cobre o requisito de idempotência: chamar
 * updateRecord/softDeleteRecord duas vezes seguidas para o mesmo registro,
 * antes do próximo sync, não deve duplicar burramente a fila; a segunda
 * chamada apenas atualiza o payload pendente para o estado mais recente.
 * (`enqueue`, em ./outbox, não faz esse dedup sozinho: sempre insere uma
 * linha nova com id novo — verificado ao ler o módulo.)
 *
 * Na prática isso raramente dispara para INSERT, porque cada
 * insertRecord/insertCounterEvent gera um UUIDv7 novo — nunca reaproveita
 * um id já enfileirado.
 */
async function enqueueCoalesced(
  tx: AppDatabase,
  tableName: string,
  recordId: string,
  operation: OutboxOperation,
  payload: unknown
): Promise<void> {
  const pending = await tx
    .select({ id: outbox.id })
    .from(outbox)
    .where(
      and(
        eq(outbox.tableName, tableName),
        eq(outbox.recordId, recordId),
        eq(outbox.operation, operation),
        isNull(outbox.processedAt)
      )
    )
    .get();

  if (pending) {
    await tx.update(outbox).set({ payload: JSON.stringify(payload) }).where(eq(outbox.id, pending.id));
    return;
  }

  await enqueue(tx, tableName, recordId, operation, payload);
}

/**
 * Insere uma linha nova numa tabela de negócio e enfileira o INSERT na
 * outbox, na mesma transação. Gera o id (UUIDv7, gerado no cliente — regra
 * invíolável do projeto) e `updated_at`/`created_at` (quando a tabela tiver
 * a coluna) automaticamente; não aceite essas colunas em `data`.
 */
export async function insertRecord<TTable extends WritableTable>(
  db: AppDatabase,
  table: TTable,
  data: Omit<InferInsertModel<TTable>, 'id' | 'createdAt' | 'updatedAt'>
): Promise<InferSelectModel<TTable>> {
  const id = uuidv7();
  const now = new Date();

  const row: Record<string, unknown> = { ...data, id, updatedAt: now };
  if ('createdAt' in table) {
    row.createdAt = now;
  }

  const anyTable = asTable(table as unknown as SQLiteTable);

  return db.transaction(async (tx) => {
    await tx.insert(anyTable).values(row);

    const inserted = await tx.select().from(anyTable).where(eq(idColumnOf(anyTable), id)).get();
    if (!inserted) {
      throw new Error(`insertRecord: linha não encontrada após insert (table=${getTableName(table)}, id=${id})`);
    }

    await enqueueCoalesced(tx, getTableName(table), id, 'INSERT', mapLocalRecordToWire(anyTable, inserted));

    return inserted as InferSelectModel<TTable>;
  });
}

/**
 * Atualiza uma linha existente e enfileira o UPDATE na outbox, na mesma
 * transação. `updated_at` é sempre atualizado para "agora", sobrescrevendo
 * qualquer valor em `patch`.
 */
export async function updateRecord<TTable extends WritableTable>(
  db: AppDatabase,
  table: TTable,
  id: string,
  patch: Partial<Omit<InferInsertModel<TTable>, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<InferSelectModel<TTable>> {
  const now = new Date();
  const setValues = { ...patch, updatedAt: now };
  const anyTable = asTable(table as unknown as SQLiteTable);
  const idColumn = idColumnOf(anyTable);

  return db.transaction(async (tx) => {
    await tx.update(anyTable).set(setValues).where(eq(idColumn, id));

    const updated = await tx.select().from(anyTable).where(eq(idColumn, id)).get();
    if (!updated) {
      throw new Error(`updateRecord: registro não encontrado (table=${getTableName(table)}, id=${id})`);
    }

    await enqueueCoalesced(tx, getTableName(table), id, 'UPDATE', mapLocalRecordToWire(anyTable, updated));

    return updated as InferSelectModel<TTable>;
  });
}

/**
 * Soft delete: seta `deleted_at` (e `updated_at`) — NUNCA remove a linha
 * fisicamente (regra invíolável #3 do projeto). A outbox é enfileirada com
 * operation='UPDATE', não 'DELETE': `SyncServiceImpl.pushAll` (src/sync)
 * trata 'DELETE' como um DELETE físico de verdade no Postgres, o que
 * quebraria a mesma regra no servidor. Um soft delete é, para fins de
 * sincronização, apenas mais uma atualização de coluna.
 */
export async function softDeleteRecord<TTable extends SoftDeletableTable>(
  db: AppDatabase,
  table: TTable,
  id: string
): Promise<InferSelectModel<TTable>> {
  const now = new Date();
  const anyTable = asTable(table as unknown as SQLiteTable);
  const idColumn = idColumnOf(anyTable);

  return db.transaction(async (tx) => {
    await tx.update(anyTable).set({ deletedAt: now, updatedAt: now }).where(eq(idColumn, id));

    const deleted = await tx.select().from(anyTable).where(eq(idColumn, id)).get();
    if (!deleted) {
      throw new Error(`softDeleteRecord: registro não encontrado (table=${getTableName(table)}, id=${id})`);
    }

    await enqueueCoalesced(tx, getTableName(table), id, 'UPDATE', mapLocalRecordToWire(anyTable, deleted));

    return deleted as InferSelectModel<TTable>;
  });
}

/**
 * Garante que existe uma linha local em `profiles` para este usuário —
 * usado pelo mini-onboarding (Etapa 9) quando ele roda antes do primeiro
 * pull trazer a linha criada pelo trigger do Postgres no signup (ver
 * src/features/onboarding/profileBootstrap.ts para o fluxo completo:
 * tenta achar a linha local, tenta um pull, só cai aqui se as duas
 * falharem).
 *
 * Não reaproveita `insertRecord` (acima): `insertRecord` sempre gera um
 * UUIDv7 novo para `id`, mas aqui o `id` precisa ser exatamente o user id
 * do Supabase Auth — a mesma linha que o trigger do Postgres já criou (ou
 * vai criar) do lado do servidor.
 *
 * Por isso a outbox é enfileirada com operation='UPDATE', não 'INSERT':
 * tratamos a linha remota como a fonte de verdade que já existe. Um INSERT
 * bateria em conflito de chave primária no Postgres (o id já existe) e
 * ficaria retentando pra sempre — `pushAll` (src/sync) não tem
 * backoff/desistência, um erro permanente vira uma entrada de outbox presa
 * para sempre. Um UPDATE para um id que ainda não existe no servidor
 * simplesmente não afeta nenhuma linha (sem erro): na pior das hipóteses
 * (trigger atrasado por algum motivo) essa gravação local fica sem efeito
 * no servidor até o próximo sync bem-sucedido — aceitável porque o valor já
 * está correto localmente, e qualquer edição seguinte (ex.: a resposta real
 * do onboarding, via `updateRecord`) reenfileira o mesmo registro
 * (`enqueueCoalesced` substitui o payload pendente em vez de empilhar).
 */
export async function ensureProfileRow(db: AppDatabase, userId: string): Promise<InferSelectModel<typeof profiles>> {
  const existing = await db.select().from(profiles).where(eq(profiles.id, userId)).get();
  if (existing) {
    return existing;
  }

  const now = new Date();
  const row = { id: userId, hourlyRate: 0, defaultMarkupPct: 0, locale: 'pt-BR', updatedAt: now };

  return db.transaction(async (tx) => {
    await tx.insert(profiles).values(row);

    const inserted = await tx.select().from(profiles).where(eq(profiles.id, userId)).get();
    if (!inserted) {
      throw new Error(`ensureProfileRow: linha não encontrada após insert (id=${userId})`);
    }

    await enqueueCoalesced(tx, getTableName(profiles), userId, 'UPDATE', mapLocalRecordToWire(profiles, inserted));

    return inserted;
  });
}

/**
 * `counter_events` é append-only (regra da etapa 5): só INSERT, delta
 * sempre ±1 (também garantido por CHECK no schema SQL). Deliberadamente NÃO
 * existe updateCounterEvent/softDeleteCounterEvent — a tabela já é excluída
 * de WritableTable/SoftDeletableTable, então updateRecord/softDeleteRecord
 * nem compilam para `counterEvents`. Esta é a única forma de escrever
 * nessa tabela pelos helpers deste módulo.
 */
export async function insertCounterEvent(
  db: AppDatabase,
  data: { counterId: string; delta: 1 | -1 }
): Promise<InferSelectModel<typeof counterEvents>> {
  const id = uuidv7();
  const row = { id, counterId: data.counterId, delta: data.delta, at: new Date() };

  return db.transaction(async (tx) => {
    await tx.insert(counterEvents).values(row);

    const inserted = await tx.select().from(counterEvents).where(eq(counterEvents.id, id)).get();
    if (!inserted) {
      throw new Error(`insertCounterEvent: linha não encontrada após insert (id=${id})`);
    }

    await enqueueCoalesced(tx, getTableName(counterEvents), id, 'INSERT', mapLocalRecordToWire(counterEvents, inserted));

    return inserted;
  });
}
